import { Component, OnInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { _ } from '@icc/shared/helpers';
import { PageComponent, ModalService, ICC_PAGE_DATA } from '@icc/configurator/shared';
import { SharedFacade } from '@icc/configurator/shared';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfigurationsService } from '@icc/configurator/shared';
import { TranslateService, APP_CONFIG, AppConfigFactory } from '@icc/shared';
import { EventBusService } from '@icc/shared';
import { WindowSillsService } from '../window-sills.service';
import { IccAccessoryAccessory, IccAccessoryColor, IccColor, IccColorGroup } from '@icc/construction-elements/types';
import { PriceCassonettoService } from '@icc/shutter/configurator';
import { UnitConverterService } from '@icc/configurator/ui';
import { ColorsPageComponent } from '@icc/construction-elements/configurator';
import { Cassonetto, ICCCassonetto } from '@icc/window-door/types';
@Component({
    selector: 'icc-cassonetto-options-page',
    templateUrl: './cassonetto-options-page.component.html',
    styleUrls: ['./cassonetto-options-page.component.scss'],
    standalone: false
})
export class CassonettoOptionsPageComponent extends PageComponent implements OnInit, OnDestroy {
    public title: string = _('ACCESSORY|Opcje kasonetki');

    cassonetto: Cassonetto;
    form: FormGroup;
    focused = false;
    currency: string | null = null;
    currentPrice$ = new BehaviorSubject<string | number>('');
    private subscriptions: Subscription[] = [];
    dimensionUnit = this.unitConverterService.getUnit();
    private dimensionImageElement: HTMLElement | null = null;

    constructor(
        private translateService: TranslateService,
        private priceCassonettoService: PriceCassonettoService,
        private sharedFacade: SharedFacade,
        private modalService: ModalService,
        private fb: FormBuilder,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        @Inject(ICC_PAGE_DATA)
        public pageData: {
            cassonettoData: ICCCassonetto;
            cassonetto?: Cassonetto;
            colors: IccColor[];
            colorGroups: IccColorGroup[];
            productPricelists: Record<string, unknown>[];
        },
        private unitConverterService: UnitConverterService,
        private renderer: Renderer2
    ) {
        super();
        this.form = this.fb.group({
            count: [1],
            selectedColor: [''],
            currentWidth: [0],
            currentHeight: [0],
            currentP1: [0],
            currentP2: [0],
            withoutFrontPlate: [false],
        });
    }

    get currentWidthControl() {
        return this.form.get('currentWidth');
    }

    get currentHeightControl() {
        return this.form.get('currentHeight');
    }

    get currentP1Control() {
        return this.form.get('currentP1');
    }

    get currentP2Control() {
        return this.form.get('currentP2');
    }

    // eslint-disable-next-line max-statements
    ngOnInit() {
        if (this.pageData.cassonetto) {
            this.cassonetto = this.pageData.cassonetto;
        } else if (!this.cassonetto || !this.cassonetto.id) {
            this.cassonetto = {} as Cassonetto;
        }

        if(!this.cassonetto?.id) {
            this.setDefaultValue()
        }

        const defaultWidth = this.cassonetto.width;
        const defaultHeight = this.cassonetto.height;
        const defaultP1 = this.cassonetto.p1;
        const defaultP2 = this.cassonetto.p2;

        const minP1 = this.isCelinoMode()
            ? this.getMinHeight()
            : (this.pageData.cassonettoData.allowDepthCuttingP1
                ? this.pageData.cassonettoData.minP1Mm
                : this.pageData.cassonettoData.defaultP1 || this.pageData.cassonettoData.minP1Mm);
        const maxP1 = this.isCelinoMode()
            ? this.getMaxHeight()
            : (this.pageData.cassonettoData.allowDepthIncreaseP1
                ? this.pageData.cassonettoData.maxP1Mm
                : this.pageData.cassonettoData.defaultP1 || this.pageData.cassonettoData.maxP1Mm);

        const minP2 = this.pageData.cassonettoData.allowDepthCuttingP2
            ? this.pageData.cassonettoData.minP2Mm
            : this.pageData.cassonettoData.defaultP2 || this.pageData.cassonettoData.minP2Mm;
        const maxP2 = this.pageData.cassonettoData.allowDepthIncreaseP2
            ? this.pageData.cassonettoData.maxP2Mm
            : this.pageData.cassonettoData.defaultP2 || this.pageData.cassonettoData.minP2Mm;

        if (!this.form.get('currentWidth')?.value || this.form.get('currentWidth')?.value === 0) {
            this.form.patchValue({
                count: this.cassonetto.count || 1,
                selectedColor: this.cassonetto.color?.id || '0',
                currentWidth: defaultWidth,
                currentHeight: defaultHeight,
                currentP1: defaultP1,
                currentP2: defaultP2,
                withoutFrontPlate: this.cassonetto.noFrontPlate || false,
            }, { emitEvent: false });

            this.form.get('count')?.setValidators([Validators.min(1)]);
            this.form.get('selectedColor')?.setValidators([Validators.required]);
            this.form.get('currentWidth')?.setValidators([Validators.min(500), Validators.max(3000)]);
            this.form.get('currentHeight')?.setValidators(this.isCelinoMode() ? [] : [Validators.min(100)]);
            this.form.get('currentP1')?.setValidators([Validators.min(minP1), Validators.max(maxP1)]);
            this.form.get('currentP2')?.setValidators([Validators.min(minP2), Validators.max(maxP2)]);
        }

        if (this.subscriptions.length === 0) {
            this.subscriptions.push(
                this.sharedFacade.currency$.subscribe(currency => {
                    this.currency = currency;
                })
            );

            this.updateDimensionValidators();

            this.subscriptions.push(
                this.form.valueChanges.subscribe(() => {
                    this.currentPrice$.next(this.getPrice());
                })
            );

            this.currentPrice$.next(this.getPrice());
        }

        this.addDimensionImageToMainContainer();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.removeDimensionImageFromMainContainer();
    }

    private addDimensionImageToMainContainer() {
        if (!this.pageData.cassonettoData.dimensionPreviewImage) {
            return;
        }

        const mainContainer = document.getElementById('main-container');
        if (!mainContainer) {
            return;
        }

        const imageContainer = this.renderer.createElement('div');
        this.renderer.addClass(imageContainer, 'dimension-preview-container');
        this.renderer.setStyle(imageContainer, 'display', 'flex');
        this.renderer.setStyle(imageContainer, 'align-items', 'center');
        this.renderer.setStyle(imageContainer, 'justify-content', 'center');
        this.renderer.setStyle(imageContainer, 'padding', '20px');

        const img = this.renderer.createElement('img');
        this.renderer.setAttribute(img, 'src', '/files/cassonetto/' + this.pageData.cassonettoData.dimensionPreviewImage);
        this.renderer.setAttribute(img, 'alt', 'Wymiary kasonetki');
        this.renderer.setStyle(img, 'max-width', '100%');
        this.renderer.setStyle(img, 'height', 'auto');

        this.renderer.appendChild(imageContainer, img);

        const firstChild = mainContainer.firstChild;
        if (firstChild) {
            this.renderer.insertBefore(mainContainer, imageContainer, firstChild);
        } else {
            this.renderer.appendChild(mainContainer, imageContainer);
        }

        this.dimensionImageElement = imageContainer;
    }

    private removeDimensionImageFromMainContainer() {
        if (this.dimensionImageElement) {
            this.renderer.removeChild(this.dimensionImageElement.parentNode, this.dimensionImageElement);
            this.dimensionImageElement = null;
        }
    }

    isCelinoMode(): boolean {
        return !!this.pageData.cassonettoData.celino;
    }

    setDefaultValue() {
        this.cassonetto.name = this.pageData.cassonettoData.name;
        this.cassonetto.description = this.pageData.cassonettoData.description;
        this.cassonetto.id = this.pageData.cassonettoData.id;
        this.cassonetto.count = 1;
        this.cassonetto.color = this.getDefaultColor();
        this.cassonetto.width = this.getMinWidth();
        this.cassonetto.height = this.isCelinoMode() ? 0 : this.getMinHeight();
        this.cassonetto.p1 = this.pageData.cassonettoData.defaultP1 ||
            (this.isCelinoMode() ? this.getMinHeight() : this.pageData.cassonettoData.minP1Mm) || 0;
        this.cassonetto.p2 = this.pageData.cassonettoData.defaultP2 || this.pageData.cassonettoData.minP2Mm || 0;
        this.cassonetto.image = this.pageData.cassonettoData.image;
        this.cassonetto.weight = 0;
        this.cassonetto.pricelists = this.pageData.cassonettoData.pricelists;
        this.cassonetto.celino = this.pageData.cassonettoData.celino;
    }

    getDefaultColor() {
        if (this.pageData.cassonettoData.selectedColor) {
            const selectedColorId = this.pageData.cassonettoData.selectedColor.id;
            const color = this.pageData.colors.find(c => c.id === selectedColorId);
            if (color) return {...color, isDefault: true};
        }
        if (this.pageData.cassonettoData.colors && this.pageData.cassonettoData.colors.length > 0) {
            return {...this.pageData.cassonettoData.colors[0], isDefault: true};
        }
        return {...this.pageData.colors[0], isDefault: true};
    }

    getMinWidth(): number {
        const priceData = this.getPriceDataForSelectedColor();
        if (!priceData) {
            return 1000;
        }

        if (priceData.width_min) {
            return Number(priceData.width_min);
        }

        if (priceData.data && Array.isArray(priceData.data) && priceData.data.length > 0) {
            const minWidth = Math.min(...priceData.data.map((item: any) => Number(item.width_from)));
            return minWidth;
        }

        return 1000;
    }

    getMaxWidth(): number {
        const priceData = this.getPriceDataForSelectedColor();
        if (!priceData) {
            return 1000;
        }

        if (priceData.width_max) {
            return Number(priceData.width_max);
        }

        if (priceData.data && Array.isArray(priceData.data) && priceData.data.length > 0) {
            const maxWidth = Math.max(...priceData.data.map((item: any) => Number(item.width_to)));
            return maxWidth;
        }

        return 1000;
    }

    getMinHeight(): number {
        const priceData = this.getPriceDataForSelectedColor();
        if (!priceData) {
            return 300;
        }

        if (priceData.height_min) {
            return Number(priceData.height_min);
        }

        if (priceData.data && Array.isArray(priceData.data) && priceData.data.length > 0) {
            const minHeight = Math.min(...priceData.data.map((item: any) => Number(item.height_from)));
            return minHeight;
        }

        return 300;
    }

    getMaxHeight(): number {
        const priceData = this.getPriceDataForSelectedColor();
        if (!priceData) {
            return 300;
        }

        if (priceData.height_max) {
            return Number(priceData.height_max);
        }

        if (priceData.data && Array.isArray(priceData.data) && priceData.data.length > 0) {
            const maxHeight = Math.max(...priceData.data.map((item: any) => Number(item.height_to)));
            return maxHeight;
        }

        return 300;
    }

    getPriceDataForSelectedColor() {
        const selectedColorGroups = this.cassonetto.color.colorGroups;
        const listPrice = this.pageData.cassonettoData.pricelists.find(priceList => selectedColorGroups.includes(Number(priceList.windowColorGroupId)))
        return this.pageData.productPricelists[listPrice.productPricelistId]
    }

    add() {
        if (this.form.valid) {
            this.cassonetto.count = this.form.value.count;
            this.cassonetto.width = this.form.value.currentWidth;
            this.cassonetto.height = this.form.value.currentHeight;
            this.cassonetto.p1 = this.form.value.currentP1;
            this.cassonetto.p2 = this.form.value.currentP2;
            this.cassonetto.noFrontPlate = this.form.value.withoutFrontPlate;

            this.sharedFacade.closePage({
                cassonetto: this.cassonetto,
            });
        }
    }

    canAdd() {
        return this.form.valid;
    }

    updateDimensionValidators() {
        const priceData = this.getPriceDataForSelectedColor();
        if (!priceData) {
            return;
        }

        const minWidth = this.getMinWidth();
        const maxWidth = this.getMaxWidth();
        const minHeight = this.getMinHeight();
        const maxHeight = this.getMaxHeight();

        this.form.get('currentWidth')?.setValidators([Validators.min(minWidth), Validators.max(maxWidth)]);
        if (!this.isCelinoMode()) {
            this.form.get('currentHeight')?.setValidators([Validators.min(minHeight), Validators.max(maxHeight)]);
        }

        const currentWidth = this.form.get('currentWidth')?.value;
        const currentHeight = this.form.get('currentHeight')?.value;

        if (currentWidth && currentWidth < minWidth) {
            this.form.get('currentWidth')?.setValue(minWidth, { emitEvent: false });
        } else if (currentWidth && currentWidth > maxWidth) {
            this.form.get('currentWidth')?.setValue(maxWidth, { emitEvent: false });
        }

        if (!this.isCelinoMode()) {
            if (currentHeight && currentHeight < minHeight) {
                this.form.get('currentHeight')?.setValue(minHeight, { emitEvent: false });
            } else if (currentHeight && currentHeight > maxHeight) {
                this.form.get('currentHeight')?.setValue(maxHeight, { emitEvent: false });
            }
        }

        this.form.get('currentWidth')?.updateValueAndValidity({ emitEvent: false });
        this.form.get('currentHeight')?.updateValueAndValidity({ emitEvent: false });
    }

    openModalColors() {
        const availableColors = this.pageData.cassonettoData.colors || [];
        const availableGroups = this.pageData.cassonettoData.colorGroups  || [];
        const currentColor = this.cassonetto.color;

        this.cassonetto.width = this.form.get('currentWidth')?.value;
        this.cassonetto.height = this.form.get('currentHeight')?.value;
        this.cassonetto.p1 = this.form.get('currentP1')?.value;
        this.cassonetto.p2 = this.form.get('currentP2')?.value;
        this.cassonetto.count = this.form.get('count')?.value;
        this.cassonetto.noFrontPlate = this.form.get('withoutFrontPlate')?.value;

        this.modalService
            .open({
                pageComponent: ColorsPageComponent,
                resolve: {
                    colors: () => availableColors,
                    selectedColor: () => currentColor,
                    colorGroups: () => availableGroups,
                    stepName: () => this.translateService.instant('ACCESSORY|Kolor kasonetki'),
                },
            })
            .result.then((result: { color: IccColor; colorGroup?: number | 'none' }) => {
                if (result && result.color) {
                    this.cassonetto.color = result.color;
                    this.cassonetto.color.isDefault = false;

                    this.form.get('selectedColor')?.setValue(result.color.id, { emitEvent: false });

                    this.updateDimensionValidators();

                    this.currentPrice$.next(this.getPrice());
                }
                return result;
            }, () => {});
    }

    getPrice() {
        const cassonettoForPricing = this.buildCassonettoObject();
        const price = this.calcCassonettoPrice(cassonettoForPricing);
        this.cassonetto.price = price;
        return price ?? this.translateService.instant('CONFIGURATOR|Do wyceny');
    }

    buildCassonettoObject(): Cassonetto {
        return {
            ...this.cassonetto,
            width: this.form.get('currentWidth')?.value,
            height: this.form.get('currentHeight')?.value,
            p1: this.form.get('currentP1')?.value,
            p2: this.form.get('currentP2')?.value,
            count: this.form.get('count')?.value,
            noFrontPlate: this.form.get('withoutFrontPlate')?.value,
        };
    }

    calcCassonettoPrice(cassonetto: Cassonetto) {
        return this.priceCassonettoService.cassonettoCalculatePrice(
            cassonetto,
            this.pageData.cassonettoData,
            this.pageData.productPricelists,

        );
    }
}
